import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'

import {
   useAskQueryInThread,
   useCreateThread,
   useGetMessagesForThread
} from '@flowwai-frontend/bps-officer-web/ai-chat-support/data-access'
import {
   LOADING_MESSAGE_ID,
   MessageModel,
   ThreadModel
} from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import {
   ChatBoxTextField,
   ThreadHeader,
   GET_MESSAGES_API_LIMIT,
   CHAT_MESSAGES_CONTAINER_ID,
   scrollMessageIntoView
} from '@flowwai-frontend/shared-web/ai-chat-support/ui'
import { showCRMFailureToastWithGivenMessage } from '@flowwai-frontend/bps-officer-web/common/ui'
import { ThreadEntityType } from '@flowwai-frontend/bps-officer-web/graphql'
import {
   LoadingWrapper,
   useCustomT
} from '@flowwai-frontend/shared/common-components'
import { useAccountStore } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import ThreadSubscriptionController from '../ThreadSubscriptionController/ThreadSubscriptionController'
import * as Styles from './styles'

interface Props {
   initialQuery?: string
   threadId: string | null
   threadName?: string
   onCreateThread: (threadModel: ThreadModel) => void
   onClickNewThreadButton: () => void
   renderActionButtons: () => React.ReactElement
   isChatBoxExpanded: boolean
   shouldShowChatHistoryInSidepeek: boolean
   onClickHistoryInExpandedView: () => void
}

const ThreadDetailedViewController = (props: Props): React.ReactElement => {
   const { initialQuery, threadId, threadName } = props

   const [threadModel, setThreadModel] = useState<ThreadModel | null>(null)
   const [hasMoreMessagesToFetch, setHasMoreMessagesToFetch] = useState(true)

   const t = useCustomT('common:errorMessages')
   const createThreadAPI = useCreateThread()
   const getMessagesForThreadAPI = useGetMessagesForThread()
   const askQueryInThreadAPI = useAskQueryInThread()
   const { accountId } = useAccountStore()
   const createThread = (): void => {
      if (!accountId) return
      const onSuccess = (thread: ThreadModel): void => {
         setThreadModel(thread)
         props.onCreateThread(thread)

         if (initialQuery) {
            askQuestion(initialQuery, thread)
            setHasMoreMessagesToFetch(false)
         }
      }
      createThreadAPI.triggerAPI({
         requestObj: {
            entityId: accountId,
            entityType: ThreadEntityType.Account
         },
         onSuccess,
         onFailure(args) {
            showCRMFailureToastWithGivenMessage(
               args.data?.message ?? t('somethingWentWrong')
            )
         }
      })
   }

   const getMessagesForThread = (threadId: number): void => {
      const onSuccess = (
         messages: MessageModel[],
         totalCount: number
      ): void => {
         if (messages.length < GET_MESSAGES_API_LIMIT)
            setHasMoreMessagesToFetch(false)

         if (threadModel) {
            messages.map(threadModel.addMessage)
         } else {
            const newThreadModel = ThreadModel.create({
               threadId,
               name: threadName ?? 'Untitled',
               messages
            })
            setThreadModel(newThreadModel)
         }
      }

      getMessagesForThreadAPI.triggerAPI({
         reqObj: {
            threadId,
            pagination: {
               limit: GET_MESSAGES_API_LIMIT,
               offset: threadModel?.messages.length ?? 0
            }
         },
         onSuccess,
         onFailure(args) {
            showCRMFailureToastWithGivenMessage(
               args.data?.message ?? t('somethingWentWrong')
            )
         }
      })
   }

   useEffect(() => {
      if (!threadId) {
         createThread()
         return
      }

      const parsedThreadId = Number(threadId)
      getMessagesForThread(parsedThreadId)
   }, [])

   const askQuestion = (query: string, newThreadModel?: ThreadModel): void => {
      const updatedThreadModel = threadModel ?? newThreadModel
      if (!updatedThreadModel) return

      const dummyMessageID = 123456789
      const dummyMessageModel = MessageModel.create({
         messageId: dummyMessageID,
         content: query,
         type: 'QUESTION',
         timeStamp: new Date()
      })
      updatedThreadModel.addRecentMessage(dummyMessageModel)
      updatedThreadModel.updateLoadingMessageStatus('QUERIED_TO_AI')

      setTimeout(() => {
         const messagesEl = document.getElementById(CHAT_MESSAGES_CONTAINER_ID)
         if (messagesEl) {
            messagesEl.scrollTo({
               top: messagesEl.scrollHeight,
               behavior: 'smooth'
            })
         }
      }, 600)

      askQueryInThreadAPI.triggerAPI({
         requestObj: {
            query: query,
            threadId: updatedThreadModel.threadId
         },
         onSuccess(newMessage) {
            updatedThreadModel.deleteMessage(dummyMessageID)
            updatedThreadModel.addRecentMessage(newMessage)

            const stringifiedMessageId = `MESSAGE:${LOADING_MESSAGE_ID}`
            scrollMessageIntoView(stringifiedMessageId)
         },
         onFailure(args) {
            showCRMFailureToastWithGivenMessage(
               args.data?.message ?? t('somethingWentWrong')
            )
         }
      })
   }

   const apiStatus = threadId
      ? getMessagesForThreadAPI.apiStatus
      : createThreadAPI.apiStatus

   const apiError = threadId
      ? getMessagesForThreadAPI.apiError
      : createThreadAPI.apiError

   const renderMessagesInThread = (): React.ReactElement =>
      threadModel ? (
         <ThreadSubscriptionController
            threadModel={threadModel}
            hasMoreMessagesToFetch={hasMoreMessagesToFetch}
            fetchMoreMessages={() => getMessagesForThread(threadModel.threadId)}
         />
      ) : (
         <></>
      )

   const renderChatFooter = (): React.ReactElement => (
      <div className='max-w-[816px] w-full self-center mt-auto px-[20px]'>
         <ChatBoxTextField
            onClickSend={askQuestion}
            autoFocus
            isDisabled={threadModel?.loadingMessageStatus !== null}
         />
      </div>
   )

   return (
      <div className={cn(Styles.containerStyles)}>
         <ThreadHeader
            onClickHistoryInExpandedView={props.onClickHistoryInExpandedView}
            onClickNewThreadButton={props.onClickNewThreadButton}
            isChatBoxExpanded={props.isChatBoxExpanded}
            shouldShowChatHistoryInSidepeek={
               props.shouldShowChatHistoryInSidepeek
            }
            renderActionButtons={props.renderActionButtons}
            threadName={threadModel?.name ?? threadName}
         />

         <div className='flex flex-col flex-1 overflow-hidden'>
            {threadModel ? (
               renderMessagesInThread()
            ) : (
               <LoadingWrapper apiStatus={apiStatus} apiError={apiError}>
                  {renderMessagesInThread()}
               </LoadingWrapper>
            )}
            {renderChatFooter()}
         </div>
      </div>
   )
}

export default observer(ThreadDetailedViewController)
